import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DriverWorkflow } from '@symfa-inc/providence-verizon-types';
import { HttpService } from '@core/services';
import {
  DriverManagementHttpService,
  PermittingHttpService,
} from '@core/services/http';
import { PermittingActions } from '@store/actions';
import { PermittingState } from '@store/reducers/permitting.reducer';
import { PermittingSelectors } from '@store/selectors';

type PrefetchTabsMap = Partial<
  Record<
    keyof PermittingState['tabData'],
    Array<keyof PermittingState['tabData']>
  >
>;

type Tab = keyof PermittingState['tabData'];

const assertNever = (value: never): never => {
  throw new Error(`Unexpected tab value for the prefetch: ${value}`);
};

export const useTabPrefetch = (currentTab: Tab, projectBundleID: string) => {
  const dispatch = useDispatch();

  const workflowLoaded = useSelector(
    PermittingSelectors.getTabLoadedState('workflow'),
  );
  const summaryLoaded = useSelector(
    PermittingSelectors.getTabLoadedState('summary'),
  );

  const jurisdictionAuditLoaded = useSelector(
    PermittingSelectors.getTabLoadedState('jurisdictionAudit'),
  );

  const contactsLoaded = useSelector(
    PermittingSelectors.getTabLoadedState('contacts'),
  );

  const financialLoaded = useSelector(
    PermittingSelectors.getTabLoadedState('financial'),
  );

  useEffect(() => {
    const prefetchDelay = 1000;

    const prefetchMap: PrefetchTabsMap = {
      summary: ['workflow', 'jurisdictionAudit', 'contacts', 'financial'],
      workflow: ['jurisdictionAudit', 'contacts', 'financial'],
      jurisdictionAudit: ['workflow', 'contacts', 'financial'],
      contacts: ['workflow', 'jurisdictionAudit', 'financial'],
      financial: ['workflow', 'jurisdictionAudit', 'contacts'],
    };

    const loadingStates = {
      workflow: workflowLoaded,
      summary: summaryLoaded,
      jurisdictionAudit: jurisdictionAuditLoaded,
      contacts: contactsLoaded,
      financial: financialLoaded,
    };

    const timer = setTimeout(() => {
      prefetchMap[currentTab]?.forEach(async (tabName: Tab) => {
        if (!loadingStates[tabName as keyof typeof loadingStates]) {
          dispatch(
            PermittingActions.setTabLoading.done({
              tabName,
              isLoading: true,
            }),
          );

          switch (tabName) {
            case 'workflow':
              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getPermittingWorkflowData(projectBundleID, {
                silent: true,
              });

              break;

            case 'summary':
              break;
            case 'contacts':
              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getPermittingContactsData(projectBundleID, {
                silent: true,
              });

              break;
            case 'jurisdictionAudit':
              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getPermittingJurisdictionAuditData(projectBundleID, {
                silent: true,
              });

              break;

            case 'financial':
              await HttpService.getHttpRequests(
                PermittingHttpService,
              ).getPermittingFinancialData(projectBundleID, {
                silent: true,
              });

              await HttpService.getHttpRequests(
                DriverManagementHttpService,
              ).getDriversListByWorkflow(DriverWorkflow.Permitting, {
                silent: true,
              });

              break;

            default:
              assertNever(tabName);
          }
        }
      });
    }, prefetchDelay);

    return () => clearTimeout(timer);
  }, [
    currentTab,
    workflowLoaded,
    summaryLoaded,
    jurisdictionAuditLoaded,
    contactsLoaded,
    financialLoaded,
  ]);
};
